import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { AlertTriangle, ShieldOff } from "lucide-react";

/**
 * Iter 137 — Account deletion (GDPR Art. 17).
 * Iter 197 — All hardcoded strings routed through i18n (en/de/es).
 *
 * Reused by PlayerDashboard + StreamerDashboard. Backend endpoints:
 *   GET  /me/account-deletion   → open claims + pending status
 *   POST /me/account-deletion   → request (typed DELETE + password + ack)
 *   DEL  /me/account-deletion   → cancel a scheduled deletion (grace window)
 */
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export default function AccountDeletionPanel({ role }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [ackForfeit, setAckForfeit] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/me/account-deletion")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const claims = data?.claims;
  const pending = data?.pending;
  const credits = claims?.streamer_credits;
  const creditTotal = (credits?.available || 0) + (credits?.reserved || 0);
  const hasLive = (claims?.live_contracts || 0) > 0;
  const unpaidRebates = claims?.unpaid_player_rebates || [];
  const blocked = hasLive || (role === "player" && unpaidRebates.length > 0);

  const submit = async () => {
    if (confirm !== "DELETE") { toast.error(t("deletion.toast.needDelete")); return; }
    if (!password) { toast.error(t("deletion.toast.needPassword")); return; }
    if (role === "streamer" && creditTotal > 0 && !ackForfeit) {
      toast.error(t("deletion.toast.needAck"));
      return;
    }
    if (!window.confirm(t("deletion.confirmSchedule").replace("{days}", data.grace_days))) return;
    setBusy(true);
    try {
      await api.post("/me/account-deletion", { password, confirmation: confirm, ack_credit_forfeiture: ackForfeit });
      toast.success(t("deletion.toast.scheduled"));
      setPassword(""); setConfirm(""); setAckForfeit(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const cancelPending = async () => {
    if (!window.confirm(t("deletion.confirmCancel"))) return;
    setBusy(true);
    try { await api.delete("/me/account-deletion"); toast.success(t("deletion.toast.cancelled")); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  if (loading || !claims) return null;

  return (
    <section className="border-2 border-[#F26D6D]/60 bg-[#F26D6D]/[0.04] p-6" data-testid="account-deletion-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldOff className="w-4 h-4 text-[#F26D6D]" />
        <h2 className="font-display font-bold text-lg text-[#F26D6D]">{t("deletion.title")}</h2>
      </div>

      {pending ? (
        <div className="space-y-3">
          <div className="text-sm text-[#E8ECF4]">{t("deletion.scheduledFor")} <span className="font-mono text-[#F26D6D]" data-testid="deletion-scheduled-for">{(pending.scheduled_for || "").slice(0, 10)}</span>.</div>
          <div className="text-[11px] text-[#8B97AE]">{t("deletion.disabledDuringWindow")}</div>
          <Button data-testid="deletion-cancel" onClick={cancelPending} disabled={busy}
            className="rounded-none bg-transparent border border-[#2DD4A7] text-[#2DD4A7] hover:bg-[#2DD4A7]/10 h-9 font-mono text-[11px] uppercase tracking-widest">
            {t("deletion.cancelBtn")}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-[#8B97AE] leading-relaxed">{t("deletion.desc").replace("{days}", data.grace_days)}</p>

          {/* Open-claim summary */}
          <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
            <div className="border border-white/10 p-2">
              <div className="text-[9px] uppercase tracking-widest text-[#8B97AE]">{t("deletion.stat.liveContracts")}</div>
              <div className={`text-lg ${hasLive ? "text-[#F26D6D]" : "text-[#2DD4A7]"}`} data-testid="deletion-live-contracts">{claims.live_contracts}</div>
            </div>
            {role === "streamer" && (
              <div className="border border-white/10 p-2">
                <div className="text-[9px] uppercase tracking-widest text-[#8B97AE]">{t("deletion.stat.credits")}</div>
                <div className="text-lg text-[#F5B841]" data-testid="deletion-credit-total">{fmt(creditTotal)}</div>
                <div className="text-[9px] text-[#8B97AE]">{t("deletion.avail")} {fmt(credits?.available)} · {t("deletion.resv")} {fmt(credits?.reserved)}</div>
              </div>
            )}
            {role === "player" && (
              <div className="border border-white/10 p-2">
                <div className="text-[9px] uppercase tracking-widest text-[#8B97AE]">{t("deletion.stat.unpaidRebates")}</div>
                <div className={`text-lg ${unpaidRebates.length ? "text-[#F26D6D]" : "text-[#2DD4A7]"}`} data-testid="deletion-unpaid-rebates">{unpaidRebates.length}</div>
                {unpaidRebates.length > 0 && (
                  <div className="text-[9px] text-[#F26D6D]">{t("deletion.rebateContactSupport")}</div>
                )}
              </div>
            )}
          </div>

          {blocked && (
            <div className="border border-[#F26D6D]/50 bg-[#F26D6D]/[0.08] p-3 flex items-start gap-2 text-[11px]" data-testid="deletion-blocked">
              <AlertTriangle className="w-3.5 h-3.5 text-[#F26D6D] mt-0.5" />
              <div>
                {hasLive && <div>{t("deletion.blocked.live")}</div>}
                {role === "player" && unpaidRebates.length > 0 && <div>{t("deletion.blocked.rebate")}</div>}
              </div>
            </div>
          )}

          {/* Forfeiture disclosure for streamer credits */}
          {role === "streamer" && creditTotal > 0 && !blocked && (
            <label className="flex items-start gap-2 text-[11px] text-[#E8ECF4] border border-[#F5B841]/40 bg-[#F5B841]/[0.06] p-3 cursor-pointer">
              <input type="checkbox" data-testid="deletion-ack-forfeit" checked={ackForfeit} onChange={(e) => setAckForfeit(e.target.checked)} className="mt-0.5" />
              <span>{t("deletion.forfeitAck1")}<b>{fmt(creditTotal)}</b>{t("deletion.forfeitAck2")}<b>{fmt(credits.available)}</b>{t("deletion.forfeitAck3")}<b>{fmt(credits.reserved)}</b>{t("deletion.forfeitAck4")}</span>
            </label>
          )}

          <div className={blocked ? "opacity-40 pointer-events-none" : ""}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[#F26D6D]">{t("deletion.password")}</Label>
                <Input data-testid="deletion-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="rounded-none bg-transparent border-[#F26D6D]/50 h-9 mt-1 font-mono text-sm" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[#F26D6D]">{t("deletion.confirmLabelPre")}<span className="font-mono">DELETE</span>{t("deletion.confirmLabelPost")}</Label>
                <Input data-testid="deletion-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="rounded-none bg-transparent border-[#F26D6D]/50 h-9 mt-1 font-mono text-sm" />
              </div>
            </div>
            <Button data-testid="deletion-submit" onClick={submit} disabled={busy || confirm !== "DELETE" || !password}
              className="mt-4 rounded-none bg-[#F26D6D] text-[#04150F] hover:bg-[#F26D6D]/80 h-9 px-5 font-mono text-[11px] uppercase tracking-widest disabled:opacity-30">
              {busy ? t("deletion.submitBusy") : t("deletion.submitBtn")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
